import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, InputBase, IconButton,
} from '@mui/material';
import {
  SearchOutlined, CloseOutlined, StorefrontOutlined, AddOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { PRIMARY, ASPECT } from '../constants';

// ── Layout constants ──────────────────────────────────────────────────────────
const COLS = 5;
const ROWS = 3;
const GAP  = 18;

const EXHIBITION_COLOR = '#0d7a5f';

// ── Trapezoid clip ────────────────────────────────────────────────────────────
const BoothClipDef = () => (
  <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }}>
    <defs>
      <clipPath id="ec-booth-clip" clipPathUnits="objectBoundingBox">
        <path d="M0 0.9 Q0 1 0.1 1 L0.9 1 Q1 1 1 0.9 L0.88 0.1 Q0.88 0 0.78 0 L0.22 0 Q0.12 0 0.12 0.1 Z" />
      </clipPath>
    </defs>
  </svg>
);

// ── Mini search bar ───────────────────────────────────────────────────────────
function SearchBar({ value, onChange }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(6px)',
      borderRadius: 100, px: 1.5, py: 0.5,
      border: '1px solid', borderColor: 'rgba(255,255,255,0.7)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      minWidth: 220,
      '&:focus-within': { borderColor: PRIMARY, boxShadow: `0 2px 12px ${alpha(PRIMARY, 0.18)}` },
    }}>
      <SearchOutlined sx={{ fontSize: 14, color: 'text.secondary', mr: 0.75, flexShrink: 0 }} />
      <InputBase
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search exhibitors…"
        sx={{ flex: 1, fontSize: 12, '& input': { p: 0 } }}
      />
      {value && (
        <IconButton size="small" onClick={() => onChange('')} sx={{ p: 0.2, ml: 0.5 }}>
          <CloseOutlined sx={{ fontSize: 12, color: 'text.secondary' }} />
        </IconButton>
      )}
    </Box>
  );
}

// ── Single booth card ─────────────────────────────────────────────────────────
function BoothCard({
  exhibitor, size, isSelected, isDragOver,
  onPointerDown, onDoubleClick, onDragStart, onDragEnter, onDragEnd, onDrop, onDragOver,
}) {
  const boothColor = exhibitor.boothBgColor || '#1a1a2e';

  return (
    <Box
      draggable
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onDragOver={onDragOver}
      sx={{
        width: size, height: size,
        cursor: 'grab',
        position: 'relative',
        outline: isSelected
          ? `2.5px solid ${EXHIBITION_COLOR}`
          : isDragOver
            ? `2px dashed ${alpha(EXHIBITION_COLOR, 0.7)}`
            : 'none',
        outlineOffset: 3,
        borderRadius: 1,
        transition: 'transform 0.12s',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {/* Selection indicator dot */}
      {isSelected && (
        <Box sx={{
          position: 'absolute', top: -6, right: -6, zIndex: 20,
          width: 16, height: 16, borderRadius: '50%',
          bgcolor: EXHIBITION_COLOR,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        </Box>
      )}

      {/* Drag-over tint */}
      {isDragOver && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 15,
          bgcolor: alpha(EXHIBITION_COLOR, 0.18), borderRadius: 1,
          pointerEvents: 'none',
        }} />
      )}

      {/* Trapezoid card body */}
      <Box sx={{ width: '100%', height: '100%', clipPath: 'url(#ec-booth-clip)', overflow: 'hidden' }}>
        {/* Image area */}
        <Box sx={{ position: 'relative', width: '100%', height: '80%', bgcolor: boothColor, overflow: 'hidden' }}>
          {exhibitor.boothThumbnail ? (
            <Box component="img" src={exhibitor.boothThumbnail} alt={exhibitor.name}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box sx={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.25)} 0%, ${boothColor} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <StorefrontOutlined sx={{ fontSize: size * 0.28, color: 'rgba(255,255,255,0.2)' }} />
            </Box>
          )}
        </Box>

        {/* Logo / name strip */}
        <Box sx={{ width: '100%', height: '20%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
          {exhibitor.logoUrl ? (
            <Box component="img" src={exhibitor.logoUrl} alt={exhibitor.name}
              sx={{ maxHeight: '70%', maxWidth: '85%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <Typography sx={{ fontSize: Math.max(7, size * 0.088), fontWeight: 700, color: PRIMARY, textAlign: 'center', lineHeight: 1.2 }}>
              {exhibitor.name}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── "Add exhibitor" card ──────────────────────────────────────────────────────
function AddCard({ size, onClick, isDragOver, onDragEnter, onDrop, onDragOver }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragEnter={onDragEnter}
      onDrop={onDrop}
      onDragOver={onDragOver}
      sx={{ width: size, height: size, cursor: 'pointer', clipPath: 'url(#ec-booth-clip)', overflow: 'hidden', transition: 'transform 0.15s', transform: hovered ? 'scale(0.96)' : 'scale(1)' }}
    >
      <Box sx={{
        width: '100%', height: '100%',
        border: `2px dashed ${(hovered || isDragOver) ? EXHIBITION_COLOR : alpha(EXHIBITION_COLOR, 0.4)}`,
        bgcolor: (hovered || isDragOver) ? alpha(EXHIBITION_COLOR, 0.09) : alpha(EXHIBITION_COLOR, 0.03),
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
        transition: 'all 0.15s',
      }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          bgcolor: (hovered || isDragOver) ? EXHIBITION_COLOR : alpha(EXHIBITION_COLOR, 0.15),
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        }}>
          <AddOutlined sx={{ fontSize: 16, color: (hovered || isDragOver) ? '#fff' : EXHIBITION_COLOR }} />
        </Box>
        <Typography sx={{ fontSize: Math.max(7, size * 0.085), fontWeight: 700, color: (hovered || isDragOver) ? EXHIBITION_COLOR : alpha(EXHIBITION_COLOR, 0.6), textAlign: 'center', lineHeight: 1.2 }}>
          Add Exhibitor
        </Typography>
      </Box>
    </Box>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ search, onAdd }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
      <StorefrontOutlined sx={{ fontSize: 40, color: '#b0bac8' }} />
      <Typography sx={{ fontSize: 13, color: '#7a8899', fontWeight: 600 }}>
        {search ? `No results for "${search}"` : 'No exhibitors in this hall yet'}
      </Typography>
      {!search && (
        <Box onClick={onAdd} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 0.85, borderRadius: '8px', cursor: 'pointer', bgcolor: EXHIBITION_COLOR, color: '#fff', fontSize: '0.8rem', fontWeight: 700, '&:hover': { opacity: 0.9 } }}>
          <AddOutlined sx={{ fontSize: 16 }} />
          Add first exhibitor
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ExhibitorCanvas
// ══════════════════════════════════════════════════════════════════════════════
/**
 * Props:
 *   event               — Event document
 *   exhibition          — ExhibitionPage document (grid settings + bg)
 *   exhibitors          — Exhibitor[] for this hall, in display order
 *   loading             — boolean
 *   selectedExhibitorId — string | null
 *   onSelectExhibitor   — (id: string | null) => void
 *   onReorder           — (reordered: Exhibitor[]) => void
 *   onDoubleClick       — (exhibitor) => void  → open booth editor
 *   onAddExhibitor      — () => void           → open add dialog
 */
export function ExhibitorCanvas({
  event,
  exhibition,
  exhibitors = [],
  loading,
  selectedExhibitorId,
  onSelectExhibitor,
  onReorder,
  onDoubleClick,
  onAddExhibitor,
}) {
  const [search, setSearch]       = useState('');
  const [cellSize, setCellSize]   = useState(null);
  const [cols, setCols]           = useState(COLS);
  const [dragOverId, setDragOverId] = useState(null);
  const gridRef       = useRef(null);
  const dragSrcIdRef  = useRef(null);

  const primary  = event?.branding?.primaryColor || PRIMARY;
  const hallRows = exhibition?.gridRows ?? ROWS;
  const hallGap  = exhibition?.gridGap  ?? GAP;

  // Background from exhibition settings
  const bgSx = !exhibition || exhibition.bgType === 'color' || !exhibition.bgType
    ? { bgcolor: exhibition?.bgColor || '#D1D9E0' }
    : exhibition.bgType === 'image' && exhibition.bgUrl
      ? { backgroundImage: `url(${exhibition.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { bgcolor: exhibition?.bgColor || '#D1D9E0' };

  // ResizeObserver
  useEffect(() => {
    const compute = () => {
      if (!gridRef.current) return;
      const { clientWidth: w, clientHeight: h } = gridRef.current;
      const hallCols   = exhibition?.gridCols;
      const activeCols = hallCols
        ? hallCols
        : w >= 1200 ? 5 : w >= 900 ? 4 : w >= 650 ? 3 : 2;
      setCols(activeCols);
      const byWidth  = (w - (activeCols - 1) * hallGap) / activeCols;
      const byHeight = (h - (hallRows - 1) * hallGap) / hallRows;
      setCellSize(Math.floor(Math.min(byWidth, byHeight)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (gridRef.current) ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, [exhibition?.gridCols, hallRows, hallGap]);

  // Filtered list
  const filtered = search.trim()
    ? exhibitors.filter(ex => ex.name?.toLowerCase().includes(search.toLowerCase()))
    : exhibitors;

  const perPage   = cols * hallRows;
  const pages     = [];
  for (let i = 0; i < filtered.length; i += perPage) pages.push(filtered.slice(i, i + perPage));
  const pageItems = pages[0] || [];

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, id) => {
    dragSrcIdRef.current = id;
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnter = useCallback((id) => {
    setDragOverId(id);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    const srcId = dragSrcIdRef.current;
    if (!srcId || srcId === targetId) { setDragOverId(null); return; }

    const srcIdx    = exhibitors.findIndex(ex => ex._id === srcId);
    const targetIdx = targetId === 'add'
      ? exhibitors.length
      : exhibitors.findIndex(ex => ex._id === targetId);

    if (srcIdx === -1) { setDragOverId(null); return; }

    const reordered     = [...exhibitors];
    const [moved]       = reordered.splice(srcIdx, 1);
    const insertAt      = targetIdx > srcIdx ? targetIdx - 1 : targetIdx;
    reordered.splice(Math.max(0, insertAt), 0, moved);
    const withOrder = reordered.map((ex, i) => ({ ...ex, order: i }));
    onReorder(withOrder);
    setDragOverId(null);
    dragSrcIdRef.current = null;
  }, [exhibitors, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragOverId(null);
    dragSrcIdRef.current = null;
  }, []);

  // How many placeholder slots to render (minus 1 for the add card when not searching)
  const showAddCard = !search && pageItems.length < perPage;
  const usedSlots   = pageItems.length + (showAddCard ? 1 : 0);
  const placeholders = Math.max(0, perPage - usedSlots);

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#e8edf2' }}
      onClick={() => onSelectExhibitor(null)}
    >
      <BoothClipDef />

      <Box sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* 16:9 live-page frame */}
        <Box
          sx={{
            width: '100%',
            maxWidth: `calc((100vh - 60px - 48px - 120px - 48px) / ${ASPECT})`,
            mx: 'auto',
            aspectRatio: `${1 / ASPECT}`,
            borderRadius: 2, overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            position: 'relative', display: 'flex', flexDirection: 'column',
            ...bgSx,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Navbar chrome */}
          <Box sx={{ height: 32, bgcolor: 'rgba(255,255,255,0.18)', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0, display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.5)' }} />
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.35)' }} />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ height: 10, width: 50, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.25)' }} />
          </Box>

          {/* Search bar */}
          <Box sx={{ flexShrink: 0, py: 1, display: 'flex', justifyContent: 'center' }}>
            <SearchBar value={search} onChange={setSearch} />
          </Box>

          {/* Grid */}
          <Box
            ref={gridRef}
            onClick={e => { if (e.target === e.currentTarget) onSelectExhibitor(null); }}
            sx={{ flex: 1, overflow: 'hidden', px: 4, pb: 2, display: 'flex', flexDirection: 'column' }}
          >
            {loading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: primary }} />
              </Box>
            ) : filtered.length === 0 && !search ? (
              <EmptyState search={search} onAdd={onAddExhibitor} />
            ) : filtered.length === 0 ? (
              <EmptyState search={search} onAdd={null} />
            ) : cellSize ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
                gridTemplateRows: `repeat(${hallRows}, ${cellSize}px)`,
                gap: `${hallGap}px`,
                justifyContent: 'center',
                alignContent: 'center',
                height: '100%',
              }}>
                {/* Exhibitor cards */}
                {pageItems.map(ex => (
                  <BoothCard
                    key={ex._id}
                    exhibitor={ex}
                    size={cellSize}
                    isSelected={selectedExhibitorId === ex._id}
                    isDragOver={dragOverId === ex._id}
                    onPointerDown={e => { e.stopPropagation(); onSelectExhibitor(ex._id); }}
                    onDoubleClick={e => { e.stopPropagation(); onDoubleClick(ex); }}
                    onDragStart={e => handleDragStart(e, ex._id)}
                    onDragEnter={() => handleDragEnter(ex._id)}
                    onDragEnd={handleDragEnd}
                    onDrop={e => handleDrop(e, ex._id)}
                    onDragOver={handleDragOver}
                  />
                ))}

                {/* Add card — immediately after last exhibitor */}
                {showAddCard && (
                  <AddCard
                    size={cellSize}
                    isDragOver={dragOverId === 'add'}
                    onClick={e => { e.stopPropagation(); onAddExhibitor(); }}
                    onDragEnter={() => handleDragEnter('add')}
                    onDrop={e => handleDrop(e, 'add')}
                    onDragOver={handleDragOver}
                  />
                )}

                {/* Empty placeholder slots */}
                {Array.from({ length: placeholders }).map((_, i) => (
                  <Box key={`ph-${i}`} sx={{ width: cellSize, height: cellSize }} />
                ))}
              </Box>
            ) : null}
          </Box>

          {/* Pagination dots */}
          {pages.length > 1 && (
            <Box sx={{ flexShrink: 0,  display: 'flex', justifyContent: 'center', gap: 0.75 }}>
              {pages.map((_, i) => (
                <Box key={i} sx={{ width: i === 0 ? 20 : 7, height: 7, borderRadius: 100, bgcolor: i === 0 ? primary : 'rgba(0,0,0,0.2)' }} />
              ))}
            </Box>
          )}
        </Box>

        <Typography sx={{ textAlign: 'center', fontSize: 11, color: '#9ea8b3', pb: 1 }}>
          Click to select · Drag to reorder · Double-click to edit booth hotspots
          {pages.length > 1 && ` · Showing page 1 of ${pages.length}`}
        </Typography>
      </Box>
    </Box>
  );
}